import { Injectable } from '@angular/core';
import { SettingsTranslationRequest, SettingsTranslationRequestDto } from '@modeso/aumico-lib-settings-fe';
import {
    TemplatesTranslationRequest,
    TemplatesTranslationRequestDto,
    isFli,
    isSubTotal,
    isSubfli,
} from '@modeso/aumico-lib-templates-fe';
import {
    IAccountMappingSubFLI,
    IAccountMappingTemplate,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    IAccountMappingTemplateGroupItemSubtotal,
    IAnnualReport,
    ItemType,
} from '@modeso/types__aumico-lib-annualreport-be';

@Injectable({ providedIn: 'root' })
export class TranslationMapper {
    public getSettingsTranslationRequest(
        annualReport: IAnnualReport,
        reportLanguage: string,
        targetLanguages: string[],
    ): SettingsTranslationRequest {
        const currenciesTranslationRequest: SettingsTranslationRequestDto = {
            id: annualReport.generalInformation.currency?.name,
            translation: annualReport.generalInformation.currency?.text[reportLanguage],
        };

        return {
            currencies: [currenciesTranslationRequest],
            locale: reportLanguage,
            targetLocales: targetLanguages.map((lang) => lang.toLocaleLowerCase()),
        };
    }

    public getTemplatesTranslationRequest(
        annualReport: IAnnualReport,
        balanceMappedTemplate: IAccountMappingTemplate,
        incomeMappedTemplate: IAccountMappingTemplate,
        targetLanguages: string[],
        reportLanguage?: string,
    ): TemplatesTranslationRequest {
        const balanceTemplateTranslationRequest = this.extractTemplateEntities(balanceMappedTemplate);
        const incomeTemplateTranslationRequest = this.extractTemplateEntities(incomeMappedTemplate);

        return {
            balanceTemplateId: annualReport.annualReportTemplate?.balanceTaxonomyId,
            incomeTemplateId: annualReport.annualReportTemplate?.incomeTaxonomyId,
            legalForm: annualReport.generalInformation.company.legalform?.name,
            accountingBasis: annualReport.generalInformation.accountingBasis?.name,
            balanceGroups: balanceTemplateTranslationRequest.groups,
            balanceFlis: balanceTemplateTranslationRequest.flis,
            balanceSubtotals: balanceTemplateTranslationRequest.subtotals,
            balanceSubflis: balanceTemplateTranslationRequest.subflis,
            balanceOfThatItems: balanceTemplateTranslationRequest.ofThatItems,
            incomeGroups: incomeTemplateTranslationRequest.groups,
            incomeFlis: incomeTemplateTranslationRequest.flis,
            incomeSubtotals: incomeTemplateTranslationRequest.subtotals,
            incomeSubflis: incomeTemplateTranslationRequest.subflis,
            incomeOfThatItems: incomeTemplateTranslationRequest.ofThatItems,
            locale: reportLanguage?.toLocaleLowerCase(),
            targetLocales: targetLanguages.map((lang) => lang.toLocaleLowerCase()),
        };
    }

    private extractTemplateEntities(template: IAccountMappingTemplate): {
        groups: TemplatesTranslationRequestDto[];
        flis: TemplatesTranslationRequestDto[];
        subtotals: TemplatesTranslationRequestDto[];
        subflis: TemplatesTranslationRequestDto[];
        ofThatItems: TemplatesTranslationRequestDto[];
    } {
        const groupsFallbackTranslationRequest: TemplatesTranslationRequestDto[] = [];
        const flisFallbackTranslationRequest: TemplatesTranslationRequestDto[] = [];
        const subtotalsFallbackTranslationRequest: TemplatesTranslationRequestDto[] = [];
        const subflisFallbackTranslationRequest: TemplatesTranslationRequestDto[] = [];
        const ofThatItemsFallbackTranslationRequest: TemplatesTranslationRequestDto[] = [];

        this.processGroup(
            template.group,
            groupsFallbackTranslationRequest,
            flisFallbackTranslationRequest,
            subtotalsFallbackTranslationRequest,
            subflisFallbackTranslationRequest,
            ofThatItemsFallbackTranslationRequest,
        );

        return {
            groups: groupsFallbackTranslationRequest,
            flis: flisFallbackTranslationRequest,
            subtotals: subtotalsFallbackTranslationRequest,
            subflis: subflisFallbackTranslationRequest,
            ofThatItems: ofThatItemsFallbackTranslationRequest,
        };
    }

    private processGroup(
        group: IAccountMappingTemplateGroup,
        groupsTranslationRequest: TemplatesTranslationRequestDto[],
        flisTranslationRequest: TemplatesTranslationRequestDto[],
        subtotalsTranslationRequest: TemplatesTranslationRequestDto[],
        subflisTranslationRequest: TemplatesTranslationRequestDto[],
        ofThatItemsTranslationRequest: TemplatesTranslationRequestDto[],
    ): void {
        const groupQueue: IAccountMappingTemplateGroup[] = [group];

        while (groupQueue.length > 0) {
            const currentGroup = groupQueue.shift();
            if (!currentGroup) continue;

            const { groups: innerGroups, items, ...groupWithoutNestedGroups } = currentGroup;

            groupsTranslationRequest.push(this.getGroupTranslationRequest(groupWithoutNestedGroups));

            groupQueue.push(...innerGroups);

            items.forEach((item) => {
                if (isSubTotal(item)) {
                    subtotalsTranslationRequest.push(
                        this.getItemTranslationRequest(item as IAccountMappingTemplateGroupItemSubtotal),
                    );
                }

                /* the check of undefined is due to a bug we had that leads that some of flis that got edited now no more has name*/
                if (isFli(item) && item.name != undefined) {
                    flisTranslationRequest.push(
                        this.getItemTranslationRequest(item as IAccountMappingTemplateGroupItemFLI),
                    );

                    (item as IAccountMappingTemplateGroupItemFLI).accounts.forEach((acc) => {
                        if (isSubfli(acc)) {
                            if (acc.type === ItemType.OF_THAT_ITEM) {
                                ofThatItemsTranslationRequest.push(this.getItemTranslationRequest(acc));
                            } else {
                                subflisTranslationRequest.push(
                                    this.getItemTranslationRequest(acc as IAccountMappingSubFLI),
                                );
                            }
                        }
                    });
                }
            });
        }
    }

    private getGroupTranslationRequest(group: Partial<IAccountMappingTemplateGroup>): TemplatesTranslationRequestDto {
        return {
            id: group.name,
            translation: group.title,
            totalTranslation: group.totalName,
        };
    }

    private getItemTranslationRequest(
        item: IAccountMappingTemplateGroupItemFLI | IAccountMappingTemplateGroupItemSubtotal | IAccountMappingSubFLI,
    ): TemplatesTranslationRequestDto {
        return {
            id: item.name,
            translation: item.caption,
        };
    }
}
